import type { VehicleRow } from "@/lib/vehicle-queries";

/**
 * EPA transmission strings carry internal codes ("Automatic (AV-S7)",
 * "Automatic (variable gear ratios)"). Keep the human-readable label only.
 */
export function cleanTransmission(raw: string | null): string {
  if (!raw) return "";
  const label = raw.replace(/\([^)]*\)/g, " ").replace(/\s+/g, " ").trim();
  return label.replace(/[,;]$/, "");
}

/** Correct indefinite article for a phrase ("a Automatic" -> "an Automatic"). */
export function article(word: string): string {
  return /^[aeiou]/i.test(word.trim()) ? "an" : "a";
}

/** Battery-electric rows are rated in MPGe, not MPG. */
export function isElectric(v: VehicleRow | null | undefined): boolean {
  if (!v) return false;
  const fuel = `${v.fuel_type ?? ""} ${v.fuel_type_detail ?? ""}`.toLowerCase();
  if (fuel.includes("electricity") && !fuel.includes("gasoline")) return true;
  return Boolean(v.ev_range_miles && !v.phev_blended && !v.displacement);
}

export function mpgUnit(v: VehicleRow | null | undefined): string {
  return isElectric(v) ? "MPGe" : "MPG";
}

/**
 * Plug-in hybrids store a *total* (gas + electric) range in ev_range_miles,
 * so it must never be described as electric-only driving range.
 */
export function isPhev(v: VehicleRow | null | undefined): boolean {
  if (!v) return false;
  if (v.phev_blended) return true;
  const fuel = `${v.fuel_type ?? ""} ${v.fuel_type_detail ?? ""}`.toLowerCase();
  return fuel.includes("electricity") && (fuel.includes("gas") || Boolean(v.displacement));
}

/** Coarse powertrain family used to keep peak figures comparable. */
export function powertrainClass(v: VehicleRow | null | undefined): "bev" | "phev" | "ice" {
  if (!v) return "ice";
  if (isElectric(v)) return "bev";
  if (isPhev(v)) return "phev";
  return "ice";
}

/**
 * Rows that share the reference vehicle's powertrain family. Prevents a
 * fuel-cell/EV variant's MPGe peak from being quoted on a gasoline model.
 */
export function sameFamily(vehicles: VehicleRow[], ref: VehicleRow | null | undefined): VehicleRow[] {
  const target = powertrainClass(ref);
  const matches = vehicles.filter((v) => powertrainClass(v) === target);
  return matches.length ? matches : vehicles;
}

/**
 * EPA engine_desc mixes injection acronyms with useful words
 * ("SIDI; Hybrid", "SIDI & PFI; Mild Hybrid"). Return only the readable part.
 */
export function cleanEngineDesc(raw: string | null | undefined): string {
  if (!raw) return "";
  const ACRONYMS =
    /^(sidi|pfi|mpfi|spfi|gdi|tbi|cidi|ffv|tc|sc|no-?cat|guzzler|hev|phev|bev)$/i;
  const words = raw
    .split(/[;,&/]+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .filter((p) => !p.split(/\s+/).every((w) => ACRONYMS.test(w.replace(/[^\w-]/g, ""))));
  const label = words.join(" ").replace(/\s+/g, " ").trim();
  if (!label) return "";
  // Still an unhelpful code (no digits and very short)?
  if (!/\d/.test(label) && label.replace(/[^A-Za-z]/g, "").length <= 3) return "";
  return label;
}
